import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { User, Bot, FileText, ExternalLink, Eye, Download } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
// import { ConfidenceIndicator } from '../ConfidenceIndicator'
import { DocumentPreviewModal } from '../DocumentPreviewModal'
import { useState } from 'react'
import { api } from '../../lib/api'
import { useNavigate } from 'react-router-dom'

interface ChatMessageProps {
  message: {
    id: string
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp: Date
    sources?: any[]
  }
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const [previewDocument, setPreviewDocument] = useState<any>(null)
  const navigate = useNavigate()


  const handleDownload = async (documentId: string, fileName: string, isPublic?: boolean, isReference?: boolean) => {
    try {
      // Use appropriate endpoint based on document type
      const endpoint = isReference ? 
        `/api/reference-documents/${documentId}/download` : 
        `/api/documents/${documentId}/download`
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      const url = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`flex max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'} gap-3`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isUser ? 'bg-primary-600' : 'bg-gradient-to-br from-blue-500 to-purple-600'
          }`}>
            {isUser ? (
              <User className="w-4 h-4 text-white" />
            ) : (
              <Bot className="w-4 h-4 text-white" />
            )}
          </div>
        </div>

        {/* Message content */}
        <div className="flex-1">
          <div className={`rounded-lg p-4 ${
            isUser 
              ? 'bg-primary-600 text-white' 
              : 'glass bg-white/5'
          }`}>
            {isUser ? (
              <p className="whitespace-pre-wrap">{message.content}</p>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({ node, className, children, ...props }: any) {
                      const match = /language-(\w+)/.exec(className || '')
                      const isInline = node?.position ? false : true
                      return !isInline && match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus as any}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className="bg-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                          {children}
                        </code>
                      )
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Sources with Confidence Indicators */}
          {message.sources && message.sources.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-500 font-medium">Quellen:</p>
              {/* Group sources by document_id to show unique documents only */}
              {Array.from(
                message.sources.reduce((map, source) => {
                  const key = source.document_id || source.title
                  if (!map.has(key)) {
                    map.set(key, source)
                  } else {
                    // Keep the source with highest relevance
                    const existing = map.get(key)
                    if (source.relevance > existing.relevance) {
                      map.set(key, source)
                    }
                  }
                  return map
                }, new Map()).values()
              ).map((source: any, index: number) => {
                const isReference = source.metadata?.is_reference === true
                
                return (
                  <div
                    key={index}
                    className="p-3 bg-white/5 rounded-lg border border-white/10"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FileText className="w-3 h-3" />
                        <span className="font-medium">
                          {source.title}
                        </span>
                        <span className="text-gray-600">
                          ({Math.round(source.relevance * 100)}% relevant)
                        </span>
                        {isReference && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                            Referenz
                          </span>
                        )}
                      </div>
                      
                      {/* Action buttons for all documents with document_id */}
                      {source.document_id && (
                        <div className="flex items-center gap-1">
                          {/* Preview button for reference documents */}
                          {isReference && (
                            <button
                              onClick={() => setPreviewDocument({
                                id: source.document_id,
                                title: source.title,
                                fileType: source.metadata?.file_type || '.pdf'
                              })}
                              className="p-1.5 hover:bg-white/10 rounded transition-colors"
                              title="Vorschau"
                            >
                              <Eye className="w-3 h-3" />
                            </button>
                          )}
                          {/* Download button for reference or public documents */}
                          {(isReference || source.metadata?.public) && (
                            <button
                              onClick={() => handleDownload(source.document_id, source.title, source.metadata?.public, isReference)}
                              className="p-1.5 hover:bg-white/10 rounded transition-colors"
                              title="Herunterladen"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Show description for reference documents */}
                    {isReference && source.content && (
                      <p className="text-xs text-gray-500 mt-2">{source.content}</p>
                    )}
                    
                    {/* Confidence indicator disabled - feature not fully implemented */}
                    {/* {!isReference && source.chunk_id && source.document_id && source.content && (
                      <ConfidenceIndicator
                        chunkId={source.chunk_id}
                        documentId={source.document_id}
                        content={source.content}
                        onFeedbackSubmitted={() => {
                          console.log('Feedback submitted for source:', source.title)
                        }}
                      />
                    )} */}
                  </div>
                )
              })}
            </div>
          )}

          {/* Timestamp */}
          <p className="text-xs text-gray-500 mt-1">
            {format(new Date(message.timestamp), 'HH:mm', { locale: de })}
          </p>
        </div>
      </div>
    </motion.div>
    
    {/* Document Preview Modal */}
    {previewDocument && (
      <DocumentPreviewModal
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        documentId={previewDocument.id}
        documentTitle={previewDocument.title}
        fileType={previewDocument.fileType}
      />
    )}
  </>
  )
}